import React from "react";
import { useTranslation } from "react-i18next";
import logo from "../../Assets/footerlogo.png";
import LanguageSwitcher from "../common/LanguageSwitcher";

const REF_SITE_FOOTER_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:ital,wght@0,400;0,500;0,600;0,700;0,800;1,400&display=swap');
  :root {
    --rs-ink: #163A5A;
    --rs-sky: #1A63A5;
    --rs-mint: #2AB6E8;
    --rs-secondary: #2AB6E8;
    --rs-secondary-deep: #1AA3D4;
    --rs-secondary-soft: #D9F4FC;
    --rs-bg-dark: #0E2D48;
    --rs-bg-dark-soft: #113a5a;
    --rs-bg-dark-line: rgba(42,182,232,.22);
    --rs-ff-head: 'Plus Jakarta Sans', system-ui, sans-serif;
    --rs-ff-body: 'Plus Jakarta Sans', system-ui, sans-serif;
  }
  .rsh-footer {
    background: radial-gradient(1200px 400px at 80% -20%, rgba(42,182,232,.18) 0%, transparent 60%), var(--rs-bg-dark);
    padding: 56px 40px 30px;
    font-family: var(--rs-ff-body);
  }
  .rsh-footer-inner { max-width: 1240px; margin: 0 auto; }
  .rsh-footer-grid {
    display: grid;
    grid-template-columns: 1.4fr 1fr 1fr 1fr;
    gap: 34px;
    margin-bottom: 28px;
  }
  .rsh-footer-brand { display: inline-flex; align-items: center; margin-bottom: 12px; text-decoration: none; }
  .rsh-footer-brand img { height: 33px; width: auto; display: block; }
  .rsh-footer-tagline { font-size: 13.5px; color: rgba(255,255,255,.65); line-height: 1.65; max-width: 320px; margin-bottom: 16px; }
  .rsh-footer-badges { display: flex; gap: 8px; flex-wrap: wrap; }
  .rsh-cert {
    background: rgba(255,255,255,.06);
    border: 1px solid var(--rs-bg-dark-line);
    border-radius: 100px;
    padding: 5px 10px;
    font-size: 10px;
    font-weight: 700;
    letter-spacing: .05em;
    color: rgba(186,230,253,.75);
  }
  .rsh-footer-col-title {
    font-size: 11px;
    font-weight: 700;
    color: rgba(186,230,253,.88);
    letter-spacing: .09em;
    margin-bottom: 14px;
    text-transform: uppercase;
  }
  .rsh-footer-link {
    display: block;
    font-size: 13px;
    color: rgba(255,255,255,.65);
    text-decoration: none;
    margin-bottom: 10px;
    transition: color .2s, transform .2s;
    width: fit-content;
  }
  .rsh-footer-link:hover {
    color: var(--rs-secondary-soft);
    transform: translateX(2px);
  }
  .rsh-footer-divider { border: none; border-top: 1px solid var(--rs-bg-dark-line); margin: 0 0 16px; }
  .rsh-footer-bottom {
    display: flex;
    justify-content: space-between;
    align-items: center;
    font-size: 11.5px;
    color: rgba(255,255,255,.5);
    flex-wrap: wrap;
    gap: 16px;
  }
  .rsh-footer-bottom-links {
    display: flex;
    gap: 14px;
    flex-wrap: wrap;
    align-items: center;
  }
  .rsh-footer-bottom-links a {
    text-decoration: none;
    color: rgba(186,230,253,.76);
    font-size: 11.5px;
    font-weight: 600;
    letter-spacing: .03em;
  }
  .rsh-footer-bottom-links a:hover { color: #fff; }
  
  .rsh-footer-bottom-actions {
    display: flex;
    align-items: center;
    gap: 18px;
    flex-wrap: wrap;
  }

  @media (max-width: 1024px) {
    .rsh-footer-grid { grid-template-columns: 1fr 1fr; }
  }
  @media (max-width: 600px) {
    .rsh-footer-grid { grid-template-columns: 1fr; }
    .rsh-footer { padding: 40px 20px 24px; }
    .rsh-footer-bottom { justify-content: center; text-align: center; flex-direction: column; gap: 12px; }
    .rsh-footer-bottom-actions { justify-content: center; width: 100%; }
    .rsh-footer-bottom-links { justify-content: center; }
  }
`;

function RefSiteFooter() {
  const { t } = useTranslation('footer');
  const currentYear = new Date().getFullYear();
  const badges = t('badges', { returnObjects: true }) || ["ESIGN", "eIDAS", "SOC 2", "GDPR"];

  return (
    <>
      <style>{REF_SITE_FOOTER_STYLES}</style>
      <footer className="rsh-footer">
        <div className="rsh-footer-inner">
          <div className="rsh-footer-grid">
            <div>
              <a href="/" className="rsh-footer-brand">
                <img src={logo} alt="RequireSign" />
              </a>
              <div className="rsh-footer-tagline">
                {t('tagline')}
              </div>
              <div className="rsh-footer-badges">
                {Array.isArray(badges) && badges.map((b) => (
                  <span className="rsh-cert" key={b}>
                    {b}
                  </span>
                ))}
              </div>
            </div>
            <div>
              <div className="rsh-footer-col-title">{t('columns.quickLinks')}</div>
              <a className="rsh-footer-link" href="/">{t('links.home')}</a>
              <a className="rsh-footer-link" href="/about">{t('links.about')}</a>
              <a className="rsh-footer-link" href="/pricing">{t('links.pricing')}</a>
              <a className="rsh-footer-link" href="/partners">{t('links.partners')}</a>
            </div>
            <div>
              <div className="rsh-footer-col-title">{t('columns.legal')}</div>
              <a className="rsh-footer-link" href="/security">{t('links.security')}</a>
              <a className="rsh-footer-link" href="/pricing">{t('links.pricing')}</a>
              <a className="rsh-footer-link" href="/privacy">{t('links.privacyPolicy')}</a>
              <a className="rsh-footer-link" href="/terms">{t('links.termsConditions')}</a>
            </div>
            <div>
              <div className="rsh-footer-col-title">{t('columns.contact')}</div>
              <a className="rsh-footer-link" href="/contact">
                {t('links.contactSupport')}
              </a>
              <div style={{ marginTop: "18px" }}>
                <LanguageSwitcher direction="up" />
              </div>
            </div>
          </div>
          <hr className="rsh-footer-divider" />
          <div className="rsh-footer-bottom">
            <span>{t('bottom.copyright', { year: currentYear })}</span>
            <div className="rsh-footer-bottom-actions">
              <div className="rsh-footer-bottom-links">
                <a href="/privacy">{t('bottom.privacy')}</a>
                <a href="/terms">{t('bottom.terms')}</a>
                <a href="/security">{t('bottom.security')}</a>
              </div>
            </div>
          </div>
        </div>
      </footer>
    </>
  );
}

export default RefSiteFooter;
